import { getCsrfCookie } from "helpers/api";
import { useAPI } from "hooks/api";
import { useEffect, useRef } from "react";

export const SAMPLE_API_RESPONSE = {
  onboardingComplete: true,
  inProgressActivity: null,
  od15BookingUrl:
    "https://coachconnect.gracepapers.com.au/session/34091799214/contact/209956852883?seat=28140664587",
  upcomingEvent: null,
  tier: {
    dedicated_coach: {
      role: "manager",
      sessions: [
        {
          id: "50252669547",
          name: "Platinum Coaching - Session 1 - Individual",
          date: "2026-04-07 03:00:00",
          time: "03:00:00",
          timezone: "UTC",
          vcLink: "https://meet.google.com/qme-ynwd-prd",
          status: "Booked",
          coach: {
            name: "Caroline Maillols",
            email: "caroline.maillols@gracepapers.com.au",
          },
          packageStatus: "open",
          employee: {
            name: "Manager 1 Local Test",
            rtwDate: "2026-04-01",
            plDate: "2026-08-01",
            sessionsCompleted: 0,
            sessionsBooked: 1,
            sessionsPending: 2,
            sessionsTotal: 3,
          },
          isOwnSession: true,
        },
        {
          id: "50251119892",
          name: "Platinum Coaching - Session 2 - Individual",
          date: null,
          time: null,
          timezone: null,
          vcLink: null,
          status: "Pending",
          coach: {
            name: "Caroline Maillols",
            email: "caroline.maillols@gracepapers.com.au",
          },
          packageStatus: "open",
          employee: {
            name: "Manager 1 Local Test",
            rtwDate: "2026-04-01",
            plDate: "2026-04-01",
            sessionsCompleted: 0,
            sessionsBooked: 1,
            sessionsPending: 2,
            sessionsTotal: 3,
          },
          isOwnSession: true,
        },
        {
          id: "50254131327",
          name: "Platinum Coaching - Session 3 - Individual",
          date: null,
          time: null,
          timezone: null,
          vcLink: null,
          status: "Pending",
          coach: {
            name: "Caroline Maillols",
            email: "caroline.maillols@gracepapers.com.au",
          },
          packageStatus: "open",
          employee: {
            name: "Manager 1 Local Test",
            rtwDate: "2026-04-01",
            plDate: "2026-04-01",
            sessionsCompleted: 0,
            sessionsBooked: 1,
            sessionsPending: 2,
            sessionsTotal: 3,
          },
          isOwnSession: true,
        },
        {
          id: "50573791499",
          name: "Platinum Coaching - Session 1 - Manager",
          date: null,
          time: null,
          timezone: null,
          vcLink: null,
          status: "Pending",
          coach: {
            name: "Vikas Thakur",
            email: "vikas.thakur@gracepapers.com.au",
          },
          packageStatus: "open",
          employee: {
            name: "John Doe",
            rtwDate: "2026-04-01",
            plDate: "2026-08-01",
            sessionsCompleted: 0,
            sessionsBooked: 0,
            sessionsPending: 3,
            sessionsTotal: 3,
          },
          isOwnSession: false,
        },
        {
          id: "50121394809",
          name: "Platinum Coaching - Session 1 - Manager",
          date: "2026-04-03 08:00:00",
          time: "08:00:00",
          timezone: "UTC",
          vcLink: "https://meet.google.com/win-wsov-ufo",
          status: "Booked",
          coach: {
            name: "Vikas Thakur",
            email: "vikas.thakur@gracepapers.com.au",
          },
          packageStatus: "open",
          employee: {
            name: "Parry Dew",
            rtwDate: "2026-04-01",
            plDate: "2026-08-01",
            sessionsCompleted: 1,
            sessionsBooked: 0,
            sessionsPending: 2,
            sessionsTotal: 3,
          },
          isOwnSession: false,
        },
      ],
    },
    od15: {
      role: "individual",
      sessions: [
        {
          id: "50706616141",
          name: "On Demand Coaching Session",
          date: "2026-03-30 05:30:00",
          time: "05:30:00",
          timezone: "UTC",
          vcLink: "https://meet.google.com/zct-zpey-tun",
          status: "Booked",
          coach: {
            name: "Vikas Thakur",
            email: "vikas.thakur@gracepapers.com.au",
          },
          packageStatus: "open",
          employee: {
            name: "Manager 1 Local Test",
            sessionsCompleted: 0,
            sessionsBooked: 1,
            sessionsPending: 0,
            sessionsTotal: 1,
          },
          isOwnSession: true,
        },
      ],
    },
  },
};

// ─── Mock data — comment/uncomment the scenario you want to test ──────────────
//
// To use a mock: uncomment the MOCK_ENABLED line and one ACTIVE_MOCK line below,
// then comment out both again when the real API is ready.
//
const MOCK_ENABLED = false;

// ─── Stream listener ──────────────────────────────────────────────────────────

/**
 * Opens a Server-Sent Events connection to /stream/coach-connect and calls
 * `onRefresh` whenever the server signals that fresh data is available.
 * Cleans up the EventSource on unmount or when the token changes.
 *
 * EventSource doesn't support custom headers, so the auth token is passed
 * as a query parameter. Ensure the back-end accepts ?token= on this endpoint.
 */
export function useCoachConnectStream(onRefresh) {
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    let es;
    let cancelled = false;

    getCsrfCookie().then(() => {
      if (cancelled) return; // component unmounted before cookie resolved

      const url = `${process.env.REACT_APP_API_ENDPOINT}/stream/coach-connect`;
      es = new EventSource(url, { withCredentials: true });

      es.addEventListener("refresh", () => {
        onRefreshRef.current?.();
      });

      es.onerror = (err) => {
        console.error("SSE error:", err);
      };
    });

    return () => {
      cancelled = true;
      es?.close();
    };
  }, []);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Fetches Coach Connect panel data for the authenticated user.
 *
 * Expected response shape from GET /coach-connect — see MOCKS above for the
 * full shape reference. Switch ACTIVE_MOCK at the top of this file to test
 * any scenario without the API being available.
 */
export function useCoachConnect() {
  // eslint-disable-next-line no-undef
  if (typeof MOCK_ENABLED !== "undefined" && MOCK_ENABLED) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    /*  useCoachConnectStream(() => {});
    // eslint-disable-next-line no-undef
    return { status: "fetched", data: SAMPLE_API_RESPONSE }; */
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { statefulGet } = useAPI();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const result = statefulGet("/coach-connect");

  // eslint-disable-next-line react-hooks/rules-of-hooks
  /*  useCoachConnectStream(() => {
    result.refetch?.();
  }); */

  return result;
}
